import {
	Folder,
	FolderPlus,
	Layers,
	Pencil,
	Plus,
	Star,
	Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { CategoryTreeSelect } from "../components/category-tree-select";
import {
	addFavorite,
	type CatalogCategoryTreeNode,
	createCollection,
	createCustomProduct,
	type DesignerCatalogCollection,
	type DesignerCatalogCollectionDetail,
	type DesignerCatalogFavorite,
	type DesignerCustomProduct,
	deleteCollection,
	deleteCustomProduct,
	getCategoryTree,
	getCollection,
	listCollections,
	listCustomProducts,
	listFavorites,
	removeCollectionItem,
	removeFavorite,
	updateCustomProduct,
} from "../services/catalog-api.service";

// ============================================================================
// 1. FAVORITES TAB
// ============================================================================

export function FavoritesTab() {
	const [favs, setFavs] = useState<DesignerCatalogFavorite[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function load() {
			setLoading(true);
			const data = await listFavorites();
			setFavs(data);
			setLoading(false);
		}
		void load();
	}, []);

	async function handleRemove(productId: string) {
		const success = await removeFavorite(productId);
		if (success) {
			setFavs((prev) => prev.filter((f) => f.catalogProductId !== productId));
			toast.success("Removed from favorites", {
				action: {
					label: "Undo",
					onClick: async () => {
						const res = await addFavorite(productId);
						if (res.success) {
							// Reload to get the fresh favorite data
							const data = await listFavorites();
							setFavs(data);
							toast.success("Restored to favorites");
						} else {
							toast.error("Failed to restore favorite");
						}
					},
				},
				duration: 5000,
			});
		} else {
			toast.error("Failed to remove favorite");
		}
	}

	if (loading)
		return (
			<SkeletonRows
				label="Loading favorites..."
				className="p-10"
				rowClassName="h-20 rounded-xl"
			/>
		);

	if (favs.length === 0) {
		return (
			<FloatPanel elevation={1} padding={48}>
				<div
					style={{ textAlign: "center", color: "var(--color-dashboard-muted)" }}
				>
					<Star
						className="h-10 w-10 mx-auto mb-4"
						style={{ color: "var(--color-dashboard-border)" }}
					/>
					<p>
						No favorited materials yet. Star items in the catalog to add them
						here.
					</p>
				</div>
			</FloatPanel>
		);
	}

	return (
		<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
			{favs.map((fav) => (
				<FloatPanel
					key={fav.id}
					elevation={1}
					padding={0}
					style={{
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<div
						style={{
							aspectRatio: "4/3",
							background: "var(--color-dashboard-divider)",
							position: "relative",
						}}
					>
						{fav.product.images?.[0] ? (
							<img
								src={fav.product.images[0]}
								alt={fav.product.name}
								style={{ width: "100%", height: "100%", objectFit: "cover" }}
							/>
						) : (
							<div className="flex h-full w-full items-center justify-center">
								<Layers className="h-8 w-8 text-muted-foreground/30" />
							</div>
						)}
						<button
							type="button"
							onClick={() => handleRemove(fav.catalogProductId)}
							style={{
								position: "absolute",
								right: 8,
								top: 8,
								background: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 6,
								padding: 6,
								cursor: "pointer",
								color: "var(--color-dashboard-pill-risk)",
							}}
							title="Remove Favorite"
						>
							<Trash2 className="h-4 w-4" />
						</button>
					</div>
					<div style={{ padding: 16 }}>
						<h3
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 600,
								fontSize: 16,
								color: "var(--color-dashboard-text)",
							}}
						>
							{fav.product.name}
						</h3>
						<p
							style={{
								fontSize: 13,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
							}}
						>
							{fav.product.description || "No description provided."}
						</p>
					</div>
				</FloatPanel>
			))}
		</div>
	);
}

// ============================================================================
// 2. COLLECTIONS TAB
// ============================================================================

export function CollectionsTab() {
	const [cols, setCols] = useState<DesignerCatalogCollection[]>([]);
	const [activeDetailId, setActiveDetailId] = useState<string | null>(null);
	const [detail, setDetail] = useState<DesignerCatalogCollectionDetail | null>(
		null,
	);
	const [loading, setLoading] = useState(true);

	// Create dialog
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [name, setName] = useState("");
	const [desc, setDesc] = useState("");

	useEffect(() => {
		async function load() {
			setLoading(true);
			const data = await listCollections();
			setCols(data);
			setLoading(false);
		}
		void load();
	}, []);

	useEffect(() => {
		const colId = activeDetailId;
		if (!colId) {
			setDetail(null);
			return;
		}
		async function loadDetail() {
			setLoading(true);
			const res = await getCollection(colId as string);
			setDetail(res);
			setLoading(false);
		}
		void loadDetail();
	}, [activeDetailId]);

	async function handleCreate() {
		if (!name.trim()) {
			toast.error("Collection name is required");
			return;
		}
		const res = await createCollection({
			name: name.trim(),
			description: desc.trim(),
		});
		if (res.success && res.collection) {
			toast.success("Collection created");
			setCols((prev) => [res.collection as DesignerCatalogCollection, ...prev]);
			setIsCreateOpen(false);
			setName("");
			setDesc("");
		} else {
			toast.error(res.error || "Failed to create collection");
		}
	}

	async function handleDelete(id: string, e: React.MouseEvent) {
		e.stopPropagation();
		if (!window.confirm("Are you sure you want to delete this collection?"))
			return;
		const res = await deleteCollection(id);
		if (res.success) {
			toast.success("Collection deleted");
			setCols((prev) => prev.filter((c) => c.id !== id));
		} else {
			toast.error(res.error || "Failed to delete collection");
		}
	}

	async function handleRemoveItem(productId: string) {
		if (!activeDetailId) return;
		const res = await removeCollectionItem(activeDetailId, productId);
		if (res.success) {
			toast.success("Item removed from collection");
			// Refresh details
			const details = await getCollection(activeDetailId);
			setDetail(details);
		} else {
			toast.error(res.error || "Failed to remove item");
		}
	}

	if (loading && !activeDetailId) {
		return (
			<SkeletonRows
				label="Loading collections..."
				className="p-10"
				rowClassName="h-20 rounded-xl"
			/>
		);
	}

	// Collection Details View
	if (activeDetailId && detail) {
		return (
			<div className="space-y-6">
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderBottom: "1px solid var(--color-dashboard-border)",
						paddingBottom: 16,
					}}
				>
					<div>
						<button
							type="button"
							onClick={() => setActiveDetailId(null)}
							style={{
								fontSize: 13,
								color: "var(--color-dashboard-muted)",
								background: "none",
								border: "none",
								cursor: "pointer",
								marginBottom: 8,
							}}
						>
							&larr; Back to Collections
						</button>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 600,
								fontSize: 24,
								color: "var(--color-dashboard-text)",
							}}
						>
							{detail.collection.name}
						</h2>
						{detail.collection.description && (
							<p
								style={{
									fontSize: 14,
									color: "var(--color-dashboard-muted)",
									marginTop: 4,
								}}
							>
								{detail.collection.description}
							</p>
						)}
					</div>
				</div>

				{detail.items.length === 0 ? (
					<FloatPanel elevation={1} padding={48}>
						<div
							style={{
								textAlign: "center",
								color: "var(--color-dashboard-muted)",
							}}
						>
							<Folder
								className="h-10 w-10 mx-auto mb-4"
								style={{ color: "var(--color-dashboard-border)" }}
							/>
							<p>This collection is empty. Add items from the catalog page.</p>
						</div>
					</FloatPanel>
				) : (
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{detail.items.map((item) => (
							<FloatPanel
								key={item.id}
								elevation={1}
								padding={0}
								style={{
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<div
									style={{
										aspectRatio: "4/3",
										background: "var(--color-dashboard-divider)",
										position: "relative",
									}}
								>
									{item.product.images?.[0] ? (
										<img
											src={item.product.images[0]}
											alt={item.product.name}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
									) : (
										<div className="flex h-full w-full items-center justify-center">
											<Layers className="h-8 w-8 text-muted-foreground/30" />
										</div>
									)}
									<button
										type="button"
										onClick={() => handleRemoveItem(item.catalogProductId)}
										style={{
											position: "absolute",
											right: 8,
											top: 8,
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 6,
											padding: 6,
											cursor: "pointer",
											color: "var(--color-dashboard-pill-risk)",
										}}
										title="Remove Item"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								</div>
								<div style={{ padding: 16 }}>
									<h3
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 600,
											fontSize: 16,
											color: "var(--color-dashboard-text)",
										}}
									>
										{item.product.name}
									</h3>
								</div>
							</FloatPanel>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="space-y-8">
			{/* Toolbar */}
			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<StudioButton
					type="button"
					variant="primary"
					size="sm"
					icon={FolderPlus}
					onClick={() => setIsCreateOpen(true)}
				>
					New Collection
				</StudioButton>
			</div>

			{/* Create dialog form */}
			{isCreateOpen && (
				// biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click dismiss does not require keyboard trigger
				// biome-ignore lint/a11y/noStaticElementInteractions: backdrop backdrop-blur overlay
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						background:
							"color-mix(in srgb, var(--color-dashboard-shadow-ink) 40%, transparent)",
						backdropFilter: "blur(6px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 999,
						padding: 16,
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							setIsCreateOpen(false);
						}
					}}
				>
					<FloatPanel
						elevation={2}
						padding={24}
						style={{ width: "100%", maxWidth: 460 }}
					>
						<div className="space-y-4">
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 18,
									color: "var(--color-dashboard-text)",
								}}
							>
								Create New Collection
							</h3>
							<div className="space-y-1">
								<label
									htmlFor="col-name"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Collection Name
								</label>
								<input
									id="col-name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Living Room Tiles"
									style={{
										width: "100%",
										padding: "10px 14px",
										border: "1px solid var(--color-border-default)",
										borderRadius: 7,
										background: "var(--color-dashboard-paper)",
										fontSize: 14,
										outline: "none",
									}}
								/>
							</div>
							<div className="space-y-1">
								<label
									htmlFor="col-desc"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Description (Optional)
								</label>
								<textarea
									id="col-desc"
									value={desc}
									onChange={(e) => setDesc(e.target.value)}
									placeholder="What is this collection for?"
									rows={3}
									style={{
										width: "100%",
										padding: "10px 14px",
										border: "1px solid var(--color-border-default)",
										borderRadius: 7,
										background: "var(--color-dashboard-paper)",
										fontSize: 14,
										outline: "none",
										resize: "none",
									}}
								/>
							</div>
							<div
								style={{
									display: "flex",
									gap: 12,
									justifyContent: "flex-end",
									paddingTop: 12,
								}}
							>
								<StudioButton
									type="button"
									variant="ghost"
									onClick={() => setIsCreateOpen(false)}
								>
									Cancel
								</StudioButton>
								<StudioButton
									type="button"
									variant="primary"
									onClick={handleCreate}
								>
									Create
								</StudioButton>
							</div>
						</div>
					</FloatPanel>
				</div>
			)}

			{/* Collections Grid */}
			{cols.length === 0 ? (
				<FloatPanel elevation={1} padding={48}>
					<div
						style={{
							textAlign: "center",
							color: "var(--color-dashboard-muted)",
						}}
					>
						<Folder
							className="h-10 w-10 mx-auto mb-4"
							style={{ color: "var(--color-dashboard-border)" }}
						/>
						<p>
							No collections created yet. Create a collection to organize
							catalog products.
						</p>
					</div>
				</FloatPanel>
			) : (
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{cols.map((col) => (
						<button
							key={col.id}
							type="button"
							onClick={() => setActiveDetailId(col.id)}
							style={{
								cursor: "pointer",
								border: "none",
								background: "transparent",
								width: "100%",
								padding: 0,
								textAlign: "left",
								display: "block",
							}}
						>
							<FloatPanel
								elevation={1}
								padding={20}
								style={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									justifyContent: "space-between",
								}}
							>
								<div>
									<div
										style={{
											display: "flex",
											alignItems: "flex-start",
											justifyContent: "space-between",
										}}
									>
										<Folder className="h-8 w-8 text-[var(--color-dashboard-accent-dark)] mb-4" />
										<button
											type="button"
											onClick={(e) => handleDelete(col.id, e)}
											style={{
												color: "var(--color-dashboard-pill-risk)",
												border: "none",
												background: "none",
												padding: 4,
												cursor: "pointer",
											}}
											title="Delete Collection"
										>
											<Trash2 className="h-4 w-4" />
										</button>
									</div>
									<h3
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 600,
											fontSize: 16,
											color: "var(--color-dashboard-text)",
										}}
									>
										{col.name}
									</h3>
									<p
										style={{
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
											marginTop: 4,
										}}
									>
										{col.description || "No description provided."}
									</p>
								</div>
								<div
									style={{
										borderTop: "1px solid var(--color-dashboard-border-soft)",
										marginTop: 16,
										paddingTop: 12,
										fontSize: 12,
										fontWeight: 600,
										color: "var(--color-dashboard-accent-dark)",
									}}
								>
									View Items &rarr;
								</div>
							</FloatPanel>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

// ============================================================================
// 3. CUSTOM PRODUCTS TAB
// ============================================================================

export function CustomProductsTab() {
	const [products, setProducts] = useState<DesignerCustomProduct[]>([]);
	const [categoryTree, setCategoryTree] = useState<CatalogCategoryTreeNode[]>(
		[],
	);
	const [loading, setLoading] = useState(true);

	// Create/Edit dialog form
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [editingProduct, setEditingProduct] =
		useState<DesignerCustomProduct | null>(null);
	const [name, setName] = useState("");
	const [brandName, setBrandName] = useState("");
	const [desc, setDesc] = useState("");
	const [price, setPrice] = useState("");
	const [categoryId, setCategoryId] = useState("");
	// The branch the category picker is currently stopped on, or "" when the
	// selection is a leaf (or empty). `categoryId` deliberately keeps the last
	// committed leaf while this is set, so browsing the taxonomy cannot blank a
	// product's category; this is what makes the half-finished state visible to
	// the form instead of silently saving the stale id.
	const [pendingCategoryBranchId, setPendingCategoryBranchId] = useState("");
	const [imageUrl, setImageUrl] = useState("");

	function openCreate() {
		setEditingProduct(null);
		setName("");
		setBrandName("");
		setDesc("");
		setPrice("");
		setCategoryId("");
		setPendingCategoryBranchId("");
		setImageUrl("");
		setIsFormOpen(true);
	}

	function openEdit(product: DesignerCustomProduct) {
		setEditingProduct(product);
		setName(product.name);
		setBrandName(product.brandName || "");
		setDesc(product.description || "");
		setPrice(product.price ? String(product.price / 100) : "");
		setCategoryId(product.categoryId || "");
		setPendingCategoryBranchId("");
		setImageUrl(product.imageUrls?.[0] || "");
		setIsFormOpen(true);
	}

	function closeForm() {
		setIsFormOpen(false);
		setEditingProduct(null);
		setPendingCategoryBranchId("");
	}

	useEffect(() => {
		async function load() {
			setLoading(true);
			const prods = await listCustomProducts();
			setProducts(prods);
			// `null` means the fetch failed. Nothing here distinguishes a missing
			// tree from an empty one -- the picker just shows no options either way
			// -- so an empty list is the right fallback for this screen.
			const cats = await getCategoryTree();
			setCategoryTree(cats ?? []);
			setLoading(false);
		}
		void load();
	}, []);

	async function handleSubmit() {
		if (!name.trim()) {
			toast.error("Product name is required");
			return;
		}
		// A product is filed on a leaf or on nothing; a half-finished drill-down is
		// neither. Saving here would write the previously committed leaf while the
		// picker shows a different branch, so refuse rather than mislead. The
		// button is disabled in this state too -- this is the guard for anything
		// that reaches the handler another way.
		if (pendingCategoryBranchId) {
			toast.error(
				"Narrow the category down to its deepest level, or clear it, before saving",
			);
			return;
		}
		let numericPrice: number | undefined;
		if (price.trim()) {
			const parsed = parseInt(price, 10);
			if (Number.isNaN(parsed) || parsed < 0) {
				toast.error("Please enter a valid price");
				return;
			}
			numericPrice = parsed * 100;
		}

		if (editingProduct) {
			// Update existing product
			const res = await updateCustomProduct(editingProduct.id, {
				name: name.trim(),
				description: desc.trim() || undefined,
				brandName: brandName.trim() || undefined,
				categoryId: categoryId || undefined,
				price: numericPrice,
				imageUrls: imageUrl.trim() ? [imageUrl.trim()] : [],
			});
			if (res.success && res.product) {
				toast.success("Custom product updated");
				setProducts((prev) =>
					prev.map((p) =>
						p.id === editingProduct.id
							? (res.product as DesignerCustomProduct)
							: p,
					),
				);
				closeForm();
			} else {
				toast.error(res.error || "Failed to update custom product");
			}
		} else {
			// Create new product
			const res = await createCustomProduct({
				name: name.trim(),
				description: desc.trim() || undefined,
				brandName: brandName.trim() || undefined,
				categoryId: categoryId || undefined,
				price: numericPrice,
				imageUrls: imageUrl.trim() ? [imageUrl.trim()] : [],
			});
			if (res.success && res.product) {
				toast.success("Custom product created");
				setProducts((prev) => [res.product as DesignerCustomProduct, ...prev]);
				closeForm();
			} else {
				toast.error(res.error || "Failed to create custom product");
			}
		}
	}

	async function handleDelete(id: string) {
		if (!window.confirm("Are you sure you want to delete this custom product?"))
			return;
		const res = await deleteCustomProduct(id);
		if (res.success) {
			toast.success("Custom product deleted");
			setProducts((prev) => prev.filter((p) => p.id !== id));
		} else {
			toast.error(res.error || "Failed to delete product");
		}
	}

	if (loading)
		return (
			<SkeletonRows
				label="Loading custom products..."
				className="p-10"
				rowClassName="h-20 rounded-xl"
			/>
		);

	return (
		<div className="space-y-8">
			{/* Toolbar */}
			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<StudioButton
					type="button"
					variant="primary"
					size="sm"
					icon={Plus}
					onClick={openCreate}
				>
					Add Custom Product
				</StudioButton>
			</div>

			{/* Create/Edit dialog form */}
			{isFormOpen && (
				// biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click dismiss does not require keyboard trigger
				// biome-ignore lint/a11y/noStaticElementInteractions: backdrop backdrop-blur overlay
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						background:
							"color-mix(in srgb, var(--color-dashboard-shadow-ink) 40%, transparent)",
						backdropFilter: "blur(6px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 999,
						padding: 16,
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							closeForm();
						}
					}}
				>
					<FloatPanel
						elevation={2}
						padding={24}
						style={{ width: "100%", maxWidth: 500 }}
					>
						<div className="space-y-4">
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 18,
									color: "var(--color-dashboard-text)",
								}}
							>
								{editingProduct
									? "Edit Custom Product"
									: "Create Custom Product"}
							</h3>
							<div className="grid gap-4 sm:grid-cols-2">
								<div className="space-y-1">
									<label
										htmlFor="cp-name"
										style={{
											fontSize: 13,
											fontWeight: 500,
											color: "var(--color-dashboard-muted-dark)",
										}}
									>
										Product Name
									</label>
									<input
										id="cp-name"
										type="text"
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="e.g. Handcrafted Oak Sideboard"
										style={{
											width: "100%",
											padding: "10px 14px",
											border: "1px solid var(--color-border-default)",
											borderRadius: 7,
											background: "var(--color-dashboard-paper)",
											fontSize: 14,
											outline: "none",
										}}
									/>
								</div>
								<div className="space-y-1">
									<label
										htmlFor="cp-brand"
										style={{
											fontSize: 13,
											fontWeight: 500,
											color: "var(--color-dashboard-muted-dark)",
										}}
									>
										Brand / Manufacturer
									</label>
									<input
										id="cp-brand"
										type="text"
										value={brandName}
										onChange={(e) => setBrandName(e.target.value)}
										placeholder="e.g. Local Craftsman"
										style={{
											width: "100%",
											padding: "10px 14px",
											border: "1px solid var(--color-border-default)",
											borderRadius: 7,
											background: "var(--color-dashboard-paper)",
											fontSize: 14,
											outline: "none",
										}}
									/>
								</div>
							</div>

							<div className="grid gap-4 sm:grid-cols-2">
								<div className="space-y-1">
									<label
										htmlFor="cp-price"
										style={{
											fontSize: 13,
											fontWeight: 500,
											color: "var(--color-dashboard-muted-dark)",
										}}
									>
										Indicative Price (₹)
									</label>
									<input
										id="cp-price"
										type="number"
										value={price}
										onChange={(e) => setPrice(e.target.value)}
										placeholder="e.g. 25000"
										style={{
											width: "100%",
											padding: "10px 14px",
											border: "1px solid var(--color-border-default)",
											borderRadius: 7,
											background: "var(--color-dashboard-paper)",
											fontSize: 14,
											outline: "none",
										}}
									/>
								</div>
								{/*
								 * Same drill-down as the browse filter, and for the same
								 * reason: this was a plain <select> listing every category
								 * flat, which plan 003 turned into 674 unordered options.
								 * A custom product is filed on a leaf like any other, so the
								 * leaf-only rule carries over unchanged.
								 *
								 * `mode="assign"` is the difference from browse: `onChange` is
								 * this record's setter, so a branch selection must not empty
								 * it. Drilling through Lighting to see what is there and then
								 * saving used to clear the product's category outright.
								 */}
								<CategoryTreeSelect
									tree={categoryTree}
									value={categoryId}
									onChange={setCategoryId}
									noneLabel="Select Category"
									mode="assign"
									onPendingBranchChange={setPendingCategoryBranchId}
								/>
							</div>

							<div className="space-y-1">
								<label
									htmlFor="cp-img"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Image URL
								</label>
								<input
									id="cp-img"
									type="text"
									value={imageUrl}
									onChange={(e) => setImageUrl(e.target.value)}
									placeholder="https://example.com/image.jpg"
									style={{
										width: "100%",
										padding: "10px 14px",
										border: "1px solid var(--color-border-default)",
										borderRadius: 7,
										background: "var(--color-dashboard-paper)",
										fontSize: 14,
										outline: "none",
									}}
								/>
							</div>

							<div className="space-y-1">
								<label
									htmlFor="cp-desc"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Description
								</label>
								<textarea
									id="cp-desc"
									value={desc}
									onChange={(e) => setDesc(e.target.value)}
									placeholder="Detailed specifications or description..."
									rows={3}
									style={{
										width: "100%",
										padding: "10px 14px",
										border: "1px solid var(--color-border-default)",
										borderRadius: 7,
										background: "var(--color-dashboard-paper)",
										fontSize: 14,
										outline: "none",
										resize: "none",
									}}
								/>
							</div>

							<div
								style={{
									display: "flex",
									gap: 12,
									justifyContent: "flex-end",
									paddingTop: 12,
								}}
							>
								<StudioButton type="button" variant="ghost" onClick={closeForm}>
									Cancel
								</StudioButton>
								<StudioButton
									type="button"
									variant="primary"
									// The picker's own hint says why; disabling keeps the form
									// from writing a category other than the one on screen.
									disabled={Boolean(pendingCategoryBranchId)}
									onClick={handleSubmit}
								>
									{editingProduct ? "Update Product" : "Create Product"}
								</StudioButton>
							</div>
						</div>
					</FloatPanel>
				</div>
			)}

			{/* Custom Products List */}
			{products.length === 0 ? (
				<FloatPanel elevation={1} padding={48}>
					<div
						style={{
							textAlign: "center",
							color: "var(--color-dashboard-muted)",
						}}
					>
						<Layers
							className="h-10 w-10 mx-auto mb-4"
							style={{ color: "var(--color-dashboard-border)" }}
						/>
						<p>
							No custom products added yet. Create products tailored
							specifically to your studio.
						</p>
					</div>
				</FloatPanel>
			) : (
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{products.map((prod) => (
						<FloatPanel
							key={prod.id}
							elevation={1}
							padding={0}
							style={{
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
								height: "100%",
								justifyContent: "space-between",
							}}
						>
							<div>
								<div
									style={{
										aspectRatio: "4/3",
										background: "var(--color-dashboard-divider)",
										position: "relative",
									}}
								>
									{prod.imageUrls?.[0] ? (
										<img
											src={prod.imageUrls[0]}
											alt={prod.name}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
									) : (
										<div className="flex h-full w-full items-center justify-center">
											<Layers className="h-8 w-8 text-muted-foreground/30" />
										</div>
									)}
									<button
										type="button"
										onClick={() => openEdit(prod)}
										style={{
											position: "absolute",
											right: 52,
											top: 8,
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 6,
											padding: 6,
											cursor: "pointer",
											color: "var(--color-dashboard-muted-dark)",
										}}
										title="Edit Custom Product"
									>
										<Pencil className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={() => handleDelete(prod.id)}
										style={{
											position: "absolute",
											right: 8,
											top: 8,
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 6,
											padding: 6,
											cursor: "pointer",
											color: "var(--color-dashboard-pill-risk)",
										}}
										title="Delete Custom Product"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								</div>
								<div style={{ padding: 16 }}>
									<div className="flex items-start justify-between">
										<h3
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 600,
												fontSize: 16,
												color: "var(--color-dashboard-text)",
											}}
										>
											{prod.name}
										</h3>
										{prod.price && (
											<span
												style={{
													fontSize: 14,
													fontWeight: 700,
													color: "var(--color-dashboard-accent-dark)",
													fontFamily: "var(--font-mono)",
												}}
											>
												₹{prod.price / 100}
											</span>
										)}
									</div>
									<p
										style={{
											fontSize: 12,
											color: "var(--color-dashboard-accent-dark)",
											fontWeight: 600,
											marginTop: 4,
										}}
									>
										Brand: {prod.brandName || "Custom Handcrafted"}
									</p>
									<p
										style={{
											fontSize: 13,
											color: "var(--color-dashboard-muted)",
											marginTop: 8,
										}}
									>
										{prod.description || "No description provided."}
									</p>
								</div>
							</div>
						</FloatPanel>
					))}
				</div>
			)}
		</div>
	);
}
